import { formatQty } from '@servwell/shared';

/**
 * A quantity with − and + a whole one at a time, and a box that takes any amount (Inventory v2:
 * putting a part back, ticket 04, and billing part of one, ticket 12a). The value is the typed
 * text; amounts are compared in hundredths. `max` is the most it may be; `min` the least: 1 (a
 * hundredth) where none would mean nothing to do, 0 where none is a choice (bill it later).
 * Stepping never goes past either; with a `min` above 0, − stops at one whole, as typing a
 * smaller amount is the deliberate way to get there.
 *
 * Hours (ticket 13) step by a quarter (`step` 25, in hundredths) and land on quarters: from 1.33,
 * − goes to 1.25 and + to 1.33's cap or 1.5. `stepName` says what one step is to a screen reader
 * ("a quarter hour").
 */
/**
 * Where one press of − or + lands, in hundredths: a whole one moves by one; a finer step lands on
 * its own multiples (1.33 h less a quarter is 1.25, plus a quarter 1.5), never past `min` or `max`.
 */
export function steppedQty(
  from: number,
  dir: 1 | -1,
  by: number,
  min: number,
  max: number,
): number {
  const to =
    by === 100
      ? from + dir * by
      : dir > 0
        ? Math.floor(from / by) * by + by
        : Math.ceil(from / by) * by - by;
  return Math.min(max, Math.max(min, to));
}

export function QtyStepper({
  value,
  onChange,
  max,
  min = 1,
  locked = false,
  label,
  of,
  errorId,
  step: by = 100,
  stepName,
}: {
  value: string;
  onChange: (next: string) => void;
  max: number;
  min?: number;
  locked?: boolean;
  /** One step, in hundredths: a whole one (100) unless said, a quarter hour (25). */
  step?: number;
  /** What one step is, said to a screen reader when it isn't a whole one ("a quarter hour"). */
  stepName?: string;
  /** What the box holds, said to a screen reader ("How many to put back"). */
  label: string;
  /** What the − and + change, when a screen has several steppers ("One more Capacitor"). */
  of?: string;
  /** The message saying why the amount can't be sent, tied to the box while it shows. */
  errorId?: string;
}) {
  const suffix = of ? ` ${of}` : '';
  const n = Math.round(Number(value) * 100);
  const valid = value.trim() !== '' && Number.isFinite(n) && n >= min && n <= max;
  const step = (dir: 1 | -1) =>
    onChange(formatQty(steppedQty(valid ? n : max, dir, by, min, max)));
  const fewerOff = locked || !valid || n <= min || (min > 0 && n <= by);
  const fewer = stepName ? `${stepName} less` : 'One fewer';
  const more = stepName ? `${stepName} more` : 'One more';
  return (
    <div className="inline-flex items-stretch overflow-hidden rounded-[var(--radius)] border border-border">
      <button
        type="button"
        aria-label={`${fewer}${suffix}`}
        className="h-12 w-12 bg-surface-2 text-xl font-semibold disabled:opacity-40"
        disabled={fewerOff}
        onClick={() => step(-1)}
      >
        −
      </button>
      <input
        aria-label={label}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        className="h-12 w-16 border-x border-border bg-surface text-center text-lg font-semibold"
        inputMode="decimal"
        value={value}
        readOnly={locked}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        aria-label={`${more}${suffix}`}
        className="h-12 w-12 bg-surface-2 text-xl font-semibold disabled:opacity-40"
        disabled={locked || (valid && n >= max)}
        onClick={() => step(1)}
      >
        +
      </button>
    </div>
  );
}
